// components/Admin/UsersList.jsx
import React, { useState } from "react";
import {
  Avatar,
  Box,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  Button,
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import axiosInstance from "../../utils/config/config";
import { useNavigate } from "react-router-dom";
import { Article, Email } from "@mui/icons-material";

const UsersList = ({ users = [], setErrorState, setSuccess, fetchTabData }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const navigate = useNavigate();

  const handleActionClick = (user) => {
    setSelectedUser(user);
    setOpenDialog(true);
  };

  const handleConfirmAction = async () => {
    try {
      if (selectedUser.status === "active") {
        const res = await axiosInstance.put(
          `/user/markUserInactive/${selectedUser.id}`
        );
        if (res) {
          setSuccess({ open: true, message: "User marked inactive." });
        }
      } else {
        const res = await axiosInstance.put(
          `/user/markUserActive/${selectedUser.id}`
        );
        if (res) {
          setSuccess({ open: true, message: "User marked active." });
        }
      }
    } catch (error) {
      setErrorState({ error: true, message: "Error updating user status." });
    } finally {
      setOpenDialog(false);
      setSelectedUser(null);
      fetchTabData();
    }
  };

  const sendResetPasswordEmail = async (id) => {
    try {
      const response = await axiosInstance.post("/user/sendResetPassword", {
        userId: id,
      });

      if (response.status === 200) {
        setErrorState({
          error: true,
          message: "Reset Password Email sent to user.",
        });
      }
    } catch (error) {
      console.log(error);
      setErrorState({
        error: true,
        message: "Error sending reset password email.",
      });
    }
  };

  const handleCancelAction = () => {
    setOpenDialog(false);
    setSelectedUser(null);
  };

  return (
    <Box padding={2}>
      <Grid container spacing={2}>
        {users.map((user) => {
          const isActive = user.status === "active";
          const isCreator = !!user.creatorId;

          const actionIcon = isActive ? <BlockIcon /> : <CheckCircleIcon />;
          const tooltipText = isActive ? "Delete User" : "Undelete User";
          const color = isActive ? "error" : "success";

          return (
            <Grid item xs={12} key={user.id}>
              <Card
                elevation={2}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 2,
                  borderRadius: 3,
                  border: "1px solid #eee",
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                  <Avatar
                    src={user.avatar}
                    alt={`${user.firstName} ${user.lastName}`}
                    sx={{ width: 64, height: 64, mr: 2 }}
                  />
                  <Box>
                    <Typography variant="h6" sx={{ color: "red" }}>
                      {user.firstName} {user.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user.email}
                    </Typography>

                    <Box
                      sx={{
                        mt: 0.5,
                        display: "flex",
                        gap: 2,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Subscribed: {user.subscribed ? "Yes" : "No"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Following: {user.following || 0}
                      </Typography>
                      {isCreator && (
                        <>
                          <Typography variant="caption" color="text.secondary">
                            Posts: {user.posts || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Plan:{" "}
                            {user.subscribed
                              ? user.subscriptionPlan
                              : "Not Subscribed"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Lifetime: ${user.lifetimeEarnings?.toFixed(2) || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            This Month: $
                            {user.currentMonthEarnings?.toFixed(2) || 0}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </Typography>
                  <Tooltip title={tooltipText} arrow>
                    <IconButton
                      onClick={() => handleActionClick(user)}
                      color={color}
                    >
                      {actionIcon}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={"Send reset password email"} arrow>
                    <IconButton
                      onClick={() => sendResetPasswordEmail(user.id)}
                      color={color}
                    >
                      <Email sx={{ color: "#FC1018" }} />
                    </IconButton>
                  </Tooltip>
                  {user.creatorId && (
                    <>
                      <Tooltip title={"Visit Profile"} arrow>
                        <IconButton
                          onClick={() => navigate(`/admin/profile/${user.id}`)}
                          color={"error"}
                        >
                          <AccountCircleIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={"View Transactions"} arrow>
                        <IconButton
                          onClick={() =>
                            navigate(`/admin/creator/transactions/${user.id}`)
                          }
                          color={"error"}
                        >
                          <Article />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Dialog open={openDialog} onClose={handleCancelAction}>
        <DialogTitle>
          Confirm {selectedUser?.status === "active" ? "Ban" : "Activation"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to{" "}
            <strong>
              {selectedUser?.status === "active" ? "ban" : "activate"}
            </strong>{" "}
            <strong>
              {selectedUser?.firstName} {selectedUser?.lastName}
            </strong>
            ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAction}>Cancel</Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color={selectedUser?.status === "active" ? "error" : "success"}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersList;
